import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  BookOpen, 
  Calendar, 
  Clock, 
  ArrowRight,
  Tag,
  User,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [viewCounts, setViewCounts] = useState({});

  const articles = [
    {
      id: 123,
      slug: "gestational-diabetes-diet-pregnancy",
      title: "Gestational Diabetes Diet: Food, Nutrition & Blood Sugar Management During Pregnancy",
      excerpt: "A doctor-informed guide to eating well with gestational diabetes, covering balanced meals, carbohydrates, Indian food choices and why medical monitoring matters.",
      category: "Gestational Diabetes",
      categoryColor: "blue",
      readTime: "10 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 115,
      slug: "weight-loss-calorie-deficit-explained",
      title: "Calorie Deficit for Weight Loss: A Doctor's Practical Guide",
      excerpt: "What a calorie deficit really means, why extreme cutting backfires, and how to build a sustainable, doctor-informed approach to weight management.",
      category: "Weight Management",
      categoryColor: "blue",
      readTime: "9 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 116,
      slug: "indian-diet-for-weight-management",
      title: "Indian Diet for Weight Management: Practical and Sustainable",
      excerpt: "How to build a balanced, satisfying Indian diet for weight management using everyday foods, sensible portions and simple meal structure.",
      category: "Weight Management",
      categoryColor: "emerald",
      readTime: "8 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 117,
      slug: "visceral-fat-waist-size-health",
      title: "Visceral Fat and Waist Size: Why They Matter More Than Weight",
      excerpt: "Why waist circumference and visceral (belly) fat can tell you more about metabolic health than weight alone, and practical steps that help.",
      category: "Metabolic Health",
      categoryColor: "orange",
      readTime: "7 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 118,
      slug: "pcos-weight-management-nutrition",
      title: "PCOS and Weight: Insulin, Nutrition and Sustainable Change",
      excerpt: "How nutrition and lifestyle can support weight and metabolic health in PCOS (also written PMOS on this site), with a realistic, non-restrictive approach.",
      category: "Women's Health",
      categoryColor: "violet",
      readTime: "9 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 119,
      slug: "preconception-nutrition-couples",
      title: "Preconception Nutrition for Couples: Preparing for Pregnancy",
      excerpt: "A balanced, evidence-informed look at nutrition and lifestyle for couples planning conception, for both partners, alongside medical care.",
      category: "Women's Health",
      categoryColor: "violet",
      readTime: "8 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 120,
      slug: "diabetes-diet-plan-indian-foods",
      title: "Diabetes Diet Plan: Indian Foods, Meals and What to Eat",
      excerpt: "A practical, doctor-informed guide to building a balanced Indian diabetes diet, covering carbohydrates, protein, fibre, portion sizes and meal timing to support blood sugar management.",
      category: "Diabetes Nutrition",
      categoryColor: "blue",
      readTime: "10 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree",
      featured: true
    },
    {
      id: 121,
      slug: "prediabetes-diet-and-lifestyle",
      title: "Prediabetes Diet and Lifestyle: Practical Steps to Support Blood Sugar",
      excerpt: "Prediabetes is a signal to act early. Learn practical, evidence-informed nutrition, activity, sleep and weight-management steps that can support healthy blood glucose.",
      category: "Diabetes Nutrition",
      categoryColor: "emerald",
      readTime: "9 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 122,
      slug: "rice-roti-fruit-and-diabetes",
      title: "Rice, Roti and Fruit in Diabetes: What You Really Need to Know",
      excerpt: "Can people with diabetes eat rice, roti and fruit? A balanced, myth-busting look at carbohydrate portions, food pairing and practical choices in an Indian diet.",
      category: "Diabetes Nutrition",
      categoryColor: "blue",
      readTime: "8 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 108,
      slug: "insulin-resistance-symptoms-diet-exercise",
      title: "Insulin Resistance: Symptoms, Causes, Diet and Exercise",
      excerpt: "Learn about insulin resistance, common symptoms, risk factors, medical evaluation, nutrition, exercise, sleep and lifestyle strategies that support metabolic health.",
      category: "Metabolic Health",
      categoryColor: "emerald",
      readTime: "10 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree",
      featured: true
    },
    {
      id: 109,
      slug: "why-am-i-always-tired",
      title: "Why Am I Always Tired? Medical, Nutrition and Lifestyle Reasons for Fatigue",
      excerpt: "Feeling tired all the time? Learn about common medical, nutrition, sleep, hydration, lifestyle and exercise related causes of persistent fatigue and when to seek medical advice.",
      category: "Lifestyle",
      categoryColor: "violet",
      readTime: "9 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 101,
      slug: "strength-training-after-40",
      title: "Strength Training After 40: Why Building Muscle Matters More Than Just Losing Weight",
      excerpt: "Why maintaining muscle strength, mobility and physical function after 40 matters more than the number on the scale, and how to start safely.",
      category: "Fitness",
      categoryColor: "cyan",
      readTime: "7 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree",
      featured: true
    },
    {
      id: 102,
      slug: "how-much-exercise-do-you-need",
      title: "How Much Exercise Do You Really Need? A Doctor's Guide to Weekly Fitness",
      excerpt: "How much physical activity adults actually need each week, why consistency beats intensity, and how to build a routine you can maintain.",
      category: "Fitness",
      categoryColor: "blue",
      readTime: "8 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 103,
      slug: "sedentary-lifestyle-health-effects",
      title: "Sitting Too Much? How a Sedentary Lifestyle Affects Your Health",
      excerpt: "Why long periods of continuous sitting matter even if you exercise, and simple ways to add more movement throughout your day.",
      category: "Lifestyle",
      categoryColor: "orange",
      readTime: "7 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 104,
      slug: "walking-for-better-health",
      title: "Walking for Better Health: How Daily Walking Supports Weight, Heart Health and Fitness",
      excerpt: "Why regular walking is one of the simplest ways to become more active, how fast to walk, and how beginners can start and progress safely.",
      category: "Fitness",
      categoryColor: "emerald",
      readTime: "8 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 105,
      slug: "sleep-and-fitness",
      title: "Sleep and Fitness: Why Poor Sleep Can Make Weight Management and Exercise Harder",
      excerpt: "Sleep is an often-overlooked pillar of health. Learn how poor sleep affects weight management and exercise, and simple habits to sleep better.",
      category: "Lifestyle",
      categoryColor: "violet",
      readTime: "7 min read",
      date: "June 2026",
      author: "Dr. Pooja Sree"
    },
    {
      id: 106,
      slug: "how-much-protein-per-day-indian-diet",
      title: "How Much Protein Do You Need Every Day? A Practical Indian Diet Guide",
      excerpt: "How much protein you actually need, the best vegetarian and non-vegetarian Indian sources, and whether protein powder is necessary.",
      category: "Nutrition Guide",
      categoryColor: "amber",
      readTime: "9 min read",
      date: "June 2026",
      author: "K. Pavani"
    },
    {
      id: 107,
      slug: "dietary-fibre-indian-foods-guide",
      title: "Dietary Fibre: How Much Do You Need and the Best Indian Foods to Get It",
      excerpt: "How much fibre adults need each day, the best everyday Indian sources of fibre, and simple ways to increase your intake without changing your whole diet.",
      category: "Nutrition Guide",
      categoryColor: "yellow",
      readTime: "9 min read",
      date: "June 2026",
      author: "K. Pavani"
    },
    {
      id: 1,
      slug: "best-cooking-oil-indian-cooking-guide",
      title: "Which Cooking Oil Is Best for Daily Use? Complete Guide for Indian Cooking",
      excerpt: "Compare ghee, groundnut oil, olive oil, sunflower oil, and more based on smoke point, fat type, and health benefits for Indian cooking.",
      category: "Kitchen Essentials",
      categoryColor: "yellow",
      readTime: "10 min read",
      date: "March 2025",
      author: "Pavani Kyama",
      featured: true
    },
    {
      id: 2,
      slug: "types-of-protein-powder-comparison",
      title: "Types of Protein Powder: Whey Concentrate vs Isolate vs Plant vs Yeast Protein",
      excerpt: "Complete guide comparing whey concentrate, whey isolate, plant protein, and yeast protein. Learn which supplement is best for your fitness goals.",
      category: "Supplements Guide",
      categoryColor: "violet",
      readTime: "10 min read",
      date: "March 2025",
      author: "Pavani Kyama"
    },
    {
      id: 3,
      slug: "prawns-fish-chicken-protein-comparison",
      title: "Prawns vs Fish vs Chicken Breast: Which Is Better for Health, Fat Loss & Muscle Gain?",
      excerpt: "Scientific comparison of protein sources for muscle gain, fat loss, and overall health. Learn which protein is best for your fitness goals.",
      category: "Nutrition Guide",
      categoryColor: "amber",
      readTime: "8 min read",
      date: "February 2025",
      author: "Pavani Kyama"
    },
    {
      id: 4,
      slug: "weekend-sports-nutrition-warmup-guide",
      title: "Best Nutrition & Warm-Up Guide for Weekend Sports Players: Football, Badminton, Cricket & Paddle",
      excerpt: "Complete guide covering pre-match nutrition, warm-up routines, and post-match recovery for recreational athletes. Prevent injuries and boost performance.",
      category: "Sports Fitness",
      categoryColor: "cyan",
      readTime: "10 min read",
      date: "February 2025",
      author: "Dr. Pooja Sree"
    },
    {
      id: 5,
      slug: "best-diet-plan-after-delivery",
      title: "Best Diet Plan After Delivery: A Complete Postnatal Recovery Guide",
      excerpt: "Comprehensive postnatal nutrition covering recovery, breastfeeding diet, and gradual weight loss. Doctor-approved guidelines for new mothers.",
      category: "Postnatal Nutrition",
      categoryColor: "orange",
      readTime: "12 min read",
      date: "January 2025",
      author: "Dr. Pooja Sree"
    },
    {
      id: 6,
      slug: "best-diet-plan-for-pregnancy",
      title: "Best Diet Plan for Pregnancy: A Complete Trimester-by-Trimester Guide",
      excerpt: "Discover the optimal foods, nutrients, and meal strategies for each trimester of pregnancy. Doctor-approved guidelines for a healthy pregnancy.",
      category: "Pregnancy Nutrition",
      categoryColor: "emerald",
      readTime: "12 min read",
      date: "December 2024",
      author: "Dr. Pooja Sree"
    },
    {
      id: 7,
      slug: "fertility-nutrition-hormone-balance",
      title: "Fertility Nutrition: Foods That Support Hormone Balance and Conception",
      excerpt: "Learn how nutrition influences fertility for both men and women. Evidence-based dietary strategies to optimize your chances of conception.",
      category: "Fertility",
      categoryColor: "pink",
      readTime: "14 min read",
      date: "December 2024",
      author: "TCUBE FITNESS Team"
    },
    {
      id: 8,
      slug: "pcos-diet-plan-complete-guide",
      title: "PMOS Diet Plan: The Complete Guide to Managing Polycystic Ovary Syndrome Through Nutrition",
      excerpt: "A comprehensive, evidence-based approach to managing PMOS symptoms through diet. Includes food lists, meal planning tips, and lifestyle strategies.",
      category: "Women's Health",
      categoryColor: "blue",
      readTime: "15 min read",
      date: "December 2024",
      author: "Dr. Pooja Sree"
    }
  ];

  // Fetch all view counts on page load
  useEffect(() => {
    const fetchViewCounts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/views`);
        if (response.ok) {
          const data = await response.json();
          setViewCounts(data);
        }
      } catch (error) {
        console.error('Error fetching view counts:', error);
      }
    };
    fetchViewCounts();
  }, []);

  // Format view count for display
  const formatViews = (views) => {
    if (!views) return '600+';
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    }
    return views.toString();
  };

  return (
    <>
      <Helmet>
        <title>Health & Nutrition Blog | Expert Articles on Pregnancy, Fertility & Fitness | TCUBE FITNESS</title>
        <meta name="description" content="Expert health articles on pregnancy nutrition, fertility diet plans, PMOS management, and medical fitness. Evidence-based guides from doctors and nutritionists." />
        <meta name="keywords" content="health blog, nutrition articles, pregnancy diet blog, fertility nutrition guide, PMOS diet guide, medical nutrition blog, fitness nutrition articles" />
        <link rel="canonical" href="https://tcubefitness.com/blog" />
        <meta property="og:title" content="Health & Nutrition Blog | TCUBE FITNESS" />
        <meta property="og:description" content="Expert health articles from doctors and nutritionists on pregnancy, fertility, and medical fitness." />
        <meta property="og:url" content="https://tcubefitness.com/blog" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-28 pb-16 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
                <BookOpen className="w-4 h-4" />
                Health & Nutrition Insights
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Expert Articles for Your Health Journey
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Evidence-based insights from our medical team on pregnancy nutrition, fertility health, PMOS (previously called PCOS) management, and doctor-led fitness.
              </p>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="space-y-8">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className={`block bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden hover:border-${article.categoryColor}-500/30 transition-all hover:shadow-xl hover:shadow-${article.categoryColor}-500/5`}
                  >
                    <div className="p-6 sm:p-8">
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 bg-${article.categoryColor}-500/10 text-${article.categoryColor}-400 text-xs font-medium rounded-full`}>
                          <Tag className="w-3 h-3" />
                          {article.category}
                        </span>
                        <span className="flex items-center gap-1 text-slate-500 text-xs">
                          <Calendar className="w-3 h-3" />
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1 text-slate-500 text-xs">
                          <Clock className="w-3 h-3" />
                          {article.readTime}
                        </span>
                        <span className="flex items-center gap-1 text-slate-500 text-xs">
                          <Eye className="w-3 h-3" />
                          {formatViews(viewCounts[article.slug])} views
                        </span>
                      </div>
                      
                      <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                        {article.title}
                      </h2>
                      
                      <p className="text-slate-400 mb-4 leading-relaxed">
                        {article.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-slate-500 text-sm">
                          <User className="w-4 h-4" />
                          {article.author}
                        </span>
                        <span className={`inline-flex items-center gap-1 text-${article.categoryColor}-400 text-sm font-medium`}>
                          Read Article
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/30 via-cyan-900/30 to-blue-900/30"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Want Personalized Health Guidance?
              </h2>
              <p className="text-slate-300 mb-6">
                Our articles provide general information. For advice tailored to your specific health situation, consult with our medical team.
              </p>
              <Link
                to="/submit-details"
                className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold transition-all hover:bg-slate-100"
              >
                Book Free Consultation
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Our Programs</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/doctor-led-fitness" className="text-slate-400 hover:text-blue-400 transition-colors">Doctor-Led Fitness</Link>
                <span className="text-slate-700">|</span>
                <Link to="/pregnancy-nutrition" className="text-slate-400 hover:text-emerald-400 transition-colors">Pregnancy Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/fertility-nutrition" className="text-slate-400 hover:text-pink-400 transition-colors">Fertility Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/medical-nutrition-therapy" className="text-slate-400 hover:text-emerald-400 transition-colors">Clinical Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/packages" className="text-slate-400 hover:text-cyan-400 transition-colors">All Programs</Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BlogPage;
